import { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocalGasStationOutlinedIcon from "@mui/icons-material/LocalGasStationOutlined";
import useAuth from "../../../hooks/useAuth";
import { compareTruckRoutes } from "../../../api/truckRouteFuelService";
import {
  TruckRouteFuelRequest,
  TruckRouteFuelResult,
} from "../../../interfaces/Operations/truckRouteFuel";
import TruckRouteFuelResults from "./TruckRouteFuelResults";

interface Props {
  open: boolean;
  plannedStartingLocation?: string;
  truckUnitNumber: string;
  onClose: () => void;
}
const defaultSetup = {
  capacity: "",
  mpg: "",
  reserve: "20",
  allowance: "30",
  stopMinutes: "15",
  height: "",
  width: "",
  length: "",
  weight: "",
  axleWeight: "",
  hazmat: false,
  fuelType: "diesel",
};
type Setup = typeof defaultSetup;
function readSetup(key: string): Setup {
  try {
    const stored = JSON.parse(localStorage.getItem(key) ?? "{}");
    const clean = { ...defaultSetup };
    Object.keys(defaultSetup).forEach((name) => {
      if (typeof stored[name] === typeof defaultSetup[name as keyof Setup])
        (clean as any)[name] = stored[name];
    });
    return clean;
  } catch {
    return { ...defaultSetup };
  }
}

export default function TruckRouteFuelDialog({
  open,
  plannedStartingLocation = "",
  truckUnitNumber,
  onClose,
}: Props) {
  const { token } = useAuth();
  const setupKey = `c1tms.truckFuelSetup.v1.${truckUnitNumber || "default"}`;
  const [start, setStart] = useState(plannedStartingLocation);
  const [destination, setDestination] = useState("");
  const [startingFuel, setStartingFuel] = useState("");
  const [setup, setSetup] = useState(() => readSetup(setupKey));
  const [result, setResult] = useState<TruckRouteFuelResult>();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const active = useRef<AbortController>();
  useEffect(() => {
    active.current?.abort();
    setLoading(false);
    setResult(undefined);
    setError("");
    if (open) {
      setStart(plannedStartingLocation);
      setDestination("");
      setStartingFuel("");
      setSetup(readSetup(setupKey));
    }
    return () => active.current?.abort();
  }, [open, plannedStartingLocation, setupKey]);
  const invalidate = () => {
    active.current?.abort();
    setLoading(false);
    setResult(undefined);
    setError("");
  };
  const update = <K extends keyof Setup>(key: K, value: Setup[K]) => {
    invalidate();
    setSetup((current) => ({ ...current, [key]: value }));
  };
  const numberField = (
    key: Exclude<keyof Setup, "hazmat" | "fuelType">,
    label: string,
    helperText?: string,
  ) => (
    <TextField
      fullWidth
      type="number"
      label={label}
      value={setup[key]}
      disabled={loading}
      inputProps={{ min: 0, step: "any" }}
      onChange={(event) => update(key, event.target.value)}
      helperText={helperText}
    />
  );
  const generate = async () => {
    if (!token) return;
    setError("");
    const numeric = (value: string): number | undefined =>
      value.trim() ? Number(value) : undefined;
    const optionalNumbers = [
      setup.capacity,
      setup.mpg,
      startingFuel,
      setup.height,
      setup.width,
      setup.length,
      setup.weight,
      setup.axleWeight,
    ].map(numeric);
    if (!start.trim() || !destination.trim()) {
      setError("Enter the starting location and destination.");
      return;
    }
    if (
      optionalNumbers.some(
        (value) => value !== undefined && !Number.isFinite(value),
      ) ||
      [setup.reserve, setup.allowance, setup.stopMinutes].some(
        (value) => !value.trim() || !Number.isFinite(Number(value)),
      )
    ) {
      setError("Enter valid fuel and time settings.");
      return;
    }
    const request: TruckRouteFuelRequest = {
      startingLocation: start.trim(),
      destination: destination.trim(),
      fuelType: setup.fuelType,
      tankCapacityGallons: numeric(setup.capacity),
      estimatedMilesPerGallon: numeric(setup.mpg),
      startingFuelGallons: numeric(startingFuel),
      minimumReserveGallons: Number(setup.reserve),
      additionalTravelTimeAllowanceMinutes: Number(setup.allowance),
      fuelStopDurationMinutes: Number(setup.stopMinutes),
      restrictions: {
        heightFeet: numeric(setup.height),
        widthFeet: numeric(setup.width),
        lengthFeet: numeric(setup.length),
        weightPounds: numeric(setup.weight),
        axleWeightPounds: numeric(setup.axleWeight),
        hazardousMaterials: setup.hazmat,
      },
    };
    if (
      !Number.isInteger(request.additionalTravelTimeAllowanceMinutes) ||
      !Number.isInteger(request.fuelStopDurationMinutes)
    ) {
      setError("Time settings must be whole minutes.");
      return;
    }
    try {
      localStorage.setItem(setupKey, JSON.stringify(setup));
    } catch {
      /* Trip calculation also works without local storage. */
    }
    active.current?.abort();
    const controller = new AbortController();
    active.current = controller;
    setLoading(true);
    setResult(undefined);
    try {
      const response = await compareTruckRoutes(
        token,
        request,
        controller.signal,
      );
      if (!controller.signal.aborted && active.current === controller)
        setResult(response);
    } catch (reason) {
      if (!controller.signal.aborted && active.current === controller)
        setError(
          reason instanceof Error
            ? reason.message
            : "Unable to calculate routes.",
        );
    } finally {
      if (active.current === controller) setLoading(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      aria-labelledby="truck-fuel-title"
    >
      <DialogTitle id="truck-fuel-title">
        Truck routes &amp; fuel recommendations
      </DialogTitle>
      {loading && (
        <LinearProgress aria-label="Comparing truck routes and fuel prices" />
      )}
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography color="text.secondary">
            Pilot · Love’s · TA · Petro · Flying J
            {truckUnitNumber ? ` · Truck ${truckUnitNumber}` : ""}
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Starting location"
                  value={start}
                  disabled={loading}
                  onChange={(event) => {
                    invalidate();
                    setStart(event.target.value);
                  }}
                  helperText="City/state, full address, or latitude,longitude"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Destination"
                  value={destination}
                  disabled={loading}
                  onChange={(event) => {
                    invalidate();
                    setDestination(event.target.value);
                  }}
                  helperText="City/state or full address"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Starting fuel at origin (US gal)"
                  value={startingFuel}
                  disabled={loading}
                  inputProps={{ min: 0, step: "any" }}
                  onChange={(event) => {
                    invalidate();
                    setStartingFuel(event.target.value);
                  }}
                  helperText="Confirm for this trip to calculate purchase quantities."
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                {numberField("capacity", "Tank capacity (US gal)")}
              </Grid>
              <Grid item xs={6} sm={4}>
                {numberField("mpg", "Estimated MPG")}
              </Grid>
            </Grid>
          </Box>
          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>
                Fuel and truck settings · remembered in this browser
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  {numberField("reserve", "Minimum reserve (US gal)")}
                </Grid>
                <Grid item xs={6} sm={3}>
                  {numberField(
                    "allowance",
                    "Extra time allowance (min)",
                    "Above the fastest verified journey, including fuel stops.",
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  {numberField("stopMinutes", "Time per fuel stop (min)")}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    select
                    fullWidth
                    label="Fuel type"
                    value={setup.fuelType}
                    disabled={loading}
                    onChange={(event) => update("fuelType", event.target.value)}
                  >
                    <MenuItem value="diesel">Diesel</MenuItem>
                    <MenuItem value="regular">Regular</MenuItem>
                  </TextField>
                </Grid>
                {(
                  [
                    ["height", "Height (ft)"],
                    ["width", "Width (ft)"],
                    ["length", "Length (ft)"],
                    ["weight", "Loaded weight (lb)"],
                    ["axleWeight", "Axle weight (lb)"],
                  ] as const
                ).map(([key, label]) => (
                  <Grid item xs={6} sm={3} key={key}>
                    {numberField(key, label)}
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={setup.hazmat}
                        disabled={loading}
                        onChange={(event) =>
                          update("hazmat", event.target.checked)
                        }
                      />
                    }
                    label="General hazardous materials"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info">
                    Enter truck dimensions and loaded weight to apply
                    vehicle-specific road restrictions. Fuel quantities require
                    tank capacity, MPG and starting fuel. You can view routes
                    and stations without these fuel inputs.
                  </Alert>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          {loading && (
            <Typography variant="body2">
              Finding truck routes, collecting chain prices, and checking
              fuel-stop plans. Price searches may take several minutes.
            </Typography>
          )}
          {result && (
            <Box>
              <TruckRouteFuelResults result={result} />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          disabled={loading || !token}
          startIcon={<LocalGasStationOutlinedIcon />}
          onClick={generate}
        >
          {loading ? "Calculating…" : "Compare routes & fuel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
